import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Swords, Flag, Shield, ArrowRight, Flame } from "lucide-react";
import { api } from "../lib/api";
import { Page, PageTitle, RowsSkeleton } from "../components/UI";

export default function RivalryIndexPage() {
  const [featured, setFeatured] = useState(undefined); // undefined=loading, null=none

  useEffect(() => {
    api.get("/rivalry/featured/nations")
      .then(({ data }) => setFeatured(data.featured || null))
      .catch(() => setFeatured(null));
  }, []);

  if (featured === undefined) return <RowsSkeleton rows={4} />;

  return (
    <Page>
      <PageTitle
        eyebrow="Head-to-Head"
        title="Rivalries"
        subtitle="Every rivalry is derived from official match and duel data — win/loss records, iconic duels and trophies between two sides. Open the featured story below, or dive in from any nation or club."
      />

      {featured ? (
        <Link
          to={featured.link}
          data-testid="rivalry-featured-card"
          className="group relative block overflow-hidden rounded-2xl foil-border bg-gradient-to-r from-[#2a2410] to-[#070707] p-6 sm:p-8 mb-10 hover:border-[#E5B842]/60"
          style={{ transition: "border-color 0.2s ease" }}
        >
          <div className="absolute inset-0 mesh-grid opacity-20 pointer-events-none" />
          <div className="relative flex items-center gap-5">
            <div className="w-14 h-14 rounded-xl bg-[#E5B842]/10 border border-[#E5B842]/25 grid place-items-center shrink-0">
              <Flame className="w-7 h-7 text-[#E5B842]" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-[#E5B842] mb-1">Featured Rivalry</div>
              <div className="font-heading text-3xl sm:text-4xl font-black uppercase tracking-tight text-slate-50 leading-none">
                {featured.a.name} <span className="text-slate-500">vs</span> {featured.b.name}
              </div>
              <div className="text-sm text-slate-400 mt-2">{featured.meetings} official meetings and counting.</div>
            </div>
            <ArrowRight
              className="w-6 h-6 text-slate-500 group-hover:text-[#E5B842] group-hover:translate-x-1 shrink-0"
              style={{ transition: "color 0.2s ease, transform 0.2s ease" }}
            />
          </div>
        </Link>
      ) : (
        <div className="rounded-2xl border border-dashed border-[#2b2512] p-8 text-center text-slate-500 font-mono text-sm uppercase tracking-widest mb-10" data-testid="rivalry-featured-empty">
          No rivalry data yet — play more matches to build a head-to-head record
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-4">
        <Link
          to="/nations"
          data-testid="rivalry-explore-nations"
          className="group rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-6 hover:border-[#E5B842]/50"
          style={{ transition: "border-color 0.2s ease" }}
        >
          <div className="flex items-center justify-between">
            <Flag className="w-8 h-8 text-[#E5B842]" />
            <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-[#E5B842]" style={{ transition: "color 0.2s ease" }} />
          </div>
          <h3 className="font-heading text-2xl font-bold uppercase tracking-tight text-slate-50 mt-5">Nation Rivalries</h3>
          <p className="text-slate-400 text-sm mt-2 leading-relaxed">Open any national team to see its historic head-to-heads against rival nations.</p>
        </Link>

        <Link
          to="/clubs"
          data-testid="rivalry-explore-clubs"
          className="group rounded-xl bg-[#0f0f0f] border border-[#2b2512] p-6 hover:border-[#E5B842]/50"
          style={{ transition: "border-color 0.2s ease" }}
        >
          <div className="flex items-center justify-between">
            <Shield className="w-8 h-8 text-[#E5B842]" />
            <ArrowRight className="w-4 h-4 text-slate-600 group-hover:text-[#E5B842]" style={{ transition: "color 0.2s ease" }} />
          </div>
          <h3 className="font-heading text-2xl font-bold uppercase tracking-tight text-slate-50 mt-5">Club Rivalries</h3>
          <p className="text-slate-400 text-sm mt-2 leading-relaxed">Browse sanctioned clubs and follow the duels that define their biggest matchups.</p>
        </Link>
      </div>

      <div className="mt-8 flex items-center gap-2 text-xs font-mono uppercase tracking-[0.2em] text-slate-600">
        <Swords className="w-3.5 h-3.5" /> All figures derived from official match &amp; duel data
      </div>
    </Page>
  );
}
